<template>
  <div class="initial-iv" :class="{compact}">
    <div class="type-select-container">
      <label>Type</label>
      <md-field class="type-select">
        <md-select
          :value="initialIv.categoryType"
          placeholder="Select a type"
          :disabled="!availableCategories.length || !pending || readonly"
          @input="typeKey => updateType(typeKey)"
        >
          <md-option
            v-for="category in availableCategories"
            :key="category.key"
            :value="category.key"
          >
            {{ category.label.en }}
          </md-option>
        </md-select>
      </md-field>
    </div>

    <div v-if="initialIv.categoryType" class="other-options-container">
      <div>
        <label>Port</label>
        <help-tooltip v-if="portAmountEnabledError" type="error">
          {{ portAmountEnabledError.message }}
        </help-tooltip>
        <md-field class="port-select">
          <md-select
            :value="initialIv.portAmount"
            placeholder="Select a port"
            :disabled="!availablePorts.length || readonly"
            @input="portKey => updatePort(portKey)"
          >
            <md-option
              v-for="port in availablePorts"
              :key="port.key"
              :value="port.key"
            >
              {{ port.label.en }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div>
        <label>Location</label>
        <help-tooltip v-if="locationEnabledError" type="error">
          {{ locationEnabledError.message }}
        </help-tooltip>
        <help-tooltip v-if="siteConflictError" type="error">
          {{ siteConflictError.message }}
        </help-tooltip>
        <md-field class="site-select">
          <md-select
            :value="initialIv.site"
            placeholder="Select a location"
            :disabled="!availableSites.length || readonly"
            @input="siteKey => updateSite(siteKey)"
          >
            <md-option
              v-for="site in availableSites"
              :key="site.key"
              :value="site.key"
            >
              {{ site.label.en }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div>
        <label>Side</label>
        <help-tooltip v-if="sideEnabledError" type="error">
          {{ sideEnabledError.message }}
        </help-tooltip>
        <md-field class="side-select">
          <md-select
            :value="initialIv.side"
            placeholder="Select a side"
            :disabled="!availableSides.length || readonly"
            @input="sideKey => updateSide(sideKey)"
          >
            <md-option
              v-for="side in availableSides"
              :key="side.key"
              :value="side.key"
            >
              {{ side.label.en }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div v-if="needleAvailable">
        <label>Needle</label>
        <help-tooltip v-if="needleEnabledError" type="error">
          {{ needleEnabledError.message }}
        </help-tooltip>
        <md-field class="needle-select">
          <md-select
            :value="initialIv.needle"
            placeholder="Select a needle"
            :disabled="readonly"
            @input="needleKey => updateNeedle(needleKey)"
          >
            <md-option
              v-for="needle in availableNeedles"
              :key="needle.key"
              :value="needle.key"
            >
              {{ needle.label.en }}
            </md-option>
          </md-select>
        </md-field>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';

import iv from '~lib/common/config/iv';
import HelpTooltip from '~lib/common/components/HelpTooltip';

export default {
  name: 'initial-iv',
  components: {
    HelpTooltip,
  },
  props: {
    initialIv: {
      type: Object,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    pending: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    needleAvailable: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    availableCategories() {
      const baseCategories = iv.categories.filter(category => {
        const ivType =
          this.caseConf.iv &&
          this.caseConf.iv.types &&
          this.caseConf.iv.types.find(
            caseType => caseType.type === category.key,
          );
        if (!ivType) {
          return false;
        }
        return ivType.sites && ivType.sites.length;
      });
      // Append the on-at-start categories, which are always available
      const productType = this.$featureAvailabilityService.productType();
      const oasCategories = iv.oasCategories.filter(
        category => {
          return category.productTypes.includes(productType)
        }
      );
      return baseCategories.concat(oasCategories);
    },
    availablePorts() {
      const ports = iv.portAmounts.filter(
        portAmount =>
          this.selectedPlacement &&
          this.selectedPlacement.ports.includes(portAmount.key),
      );
      // If selected value isn't available, add that too
      if (this.initialIv.portAmount && !ports.find(pa => pa.key === this.initialIv.portAmount)) {
        ports.push(iv.portAmounts.find(pa => pa.key === this.initialIv.portAmount))
      }
      return ports;
    },
    availableSites() {
      const sites = iv.sites.filter(
        site =>
          this.selectedPlacement &&
          this.selectedPlacement.sites.some(
            placementSite => placementSite.site === site.key,
          ),
      );
      if (!sites.find(ivSite => ivSite.key === this.initialIv.site)) {
        // If previously selected site is not in the list, add it
        const site = iv.sites.find(ivSite => ivSite.key === this.initialIv.site);
        if (site) {
          sites.push(site);
        }
      }
      return sites;
    },
    availableSides() {
      const sides = iv.sides.filter(
        side =>
          this.selectedPlacementSite &&
          this.selectedPlacementSite.sides.includes(side.key),
      );
      if (!sides.find(ivSide => ivSide.key === this.initialIv.side)) {
        // If previously selected side is not in the list, add it
        const side = iv.sides.find(ivSide => ivSide.key === this.initialIv.side);
        if (side) {
          sides.push(side);
        }
      }
      return sides;
    },
    availableNeedles() {
      const allNeedles = [...iv.needleGauges, ...iv.needleSets];
      const needles = allNeedles.filter(
        gauge =>
          this.selectedPlacement &&
          this.selectedPlacement.gauges.includes(gauge.key),
      );
      if (!needles.find(ivNeedle => ivNeedle.key === this.initialIv.needle)) {
        // If previously selected needle is not in the list, add it
        const needle = allNeedles.find(ivNeedle => ivNeedle.key === this.initialIv.needle);
        if (needle) {
          needles.push(needle);
        }
      }
      return needles;
    },
    selectedPlacement() {
      if (!this.caseConf.iv) {
        return null;
      }
      let placement = this.caseConf.iv.types.find(
        placement => placement.type === this.initialIv.categoryType,
      );
      if (!placement) {
        placement = iv.oasPlacements.find(
          placement => placement.type === this.initialIv.categoryType
        );
      }
      return placement;
    },
    selectedPlacementSite() {
      if (!this.selectedPlacement) {
        return null;
      }
      return this.selectedPlacement.sites.find(
        placementSite => placementSite.site === this.initialIv.site,
      );
    },
    initialIvErrors() {
      return this.$store
        ? get(
          this.$store.getters['validation/errorsByFieldAndId'],
          `initialIvs.${this.initialIv.id}`
        ) || [] : [];
    },
    portAmountEnabledError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'portAmountEnabled');
    },
    locationEnabledError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'locationEnabled');
    },
    sideEnabledError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'sideEnabled');
    },
    needleEnabledError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'needleEnabled');
    },
    siteConflictError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'siteConflict');
    }
  },
  methods: {
    updateType(typeKey) {
      const needle = this.availableNeedles.length === 1 ? this.availableNeedles[0].key : null;
      this.$emit('update', {
        ...this.initialIv,
        categoryType: typeKey,
        portAmount: null,
        site: null,
        side: null,
        needle,
      });
    },
    updatePort(portKey) {
      this.$emit('update', {
        ...this.initialIv,
        portAmount: portKey,
      });
    },
    updateSite(siteKey) {
      this.$emit('update', {
        ...this.initialIv,
        site: siteKey,
      });
    },
    updateSide(sideKey) {
      this.$emit('update', {
        ...this.initialIv,
        side: sideKey,
      });
    },
    updateNeedle(needleKey) {
      this.$emit('update', {
        ...this.initialIv,
        needle: needleKey,
      });
    },
  },
};
</script>

<style scpoed>
.initial-iv {
  padding-top: 10px;
}
.other-options-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-top: 10px;
}

.initial-iv.compact {
  display: flex;
  flex-direction: row;
  padding-top: 0;
}

.initial-iv.compact .other-options-container {
  padding-top: 0;
}

.initial-iv.compact .md-field {
  width: 120px;
}

.initial-iv.compact label {
  display: none;
}

.remove-button {
  margin: 0;
  margin-top: 20px;
}
</style>
